/**
 * Plan y uso (SDD §7.2, §14): horas del período, fecha de reinicio,
 * grabaciones retenidas, mejorar (checkout de Paddle) y portal de facturación.
 */
import { useState } from 'react'
import { Link } from 'react-router'
import { PLANS, formatMinutes } from '@eco/shared'
import { useApp, useToast, errorText } from '../components/AppContext'
import { StatusBadge, UsageMeter } from '../components/ui'
import { formatDay } from '../lib/format'
import { useAsync, useDocumentTitle, usePolling } from '../lib/hooks'
import { PADDLE_CLIENT_TOKEN, SITIO_URL, openPaddleCheckout } from '../lib/thirdparty'

const clp = (n: number) => `$${n.toLocaleString('es-CL')}`

export function Plan() {
  useDocumentTitle('Plan y uso')
  const { api, usage, refreshUsage, org } = useApp()
  const toast = useToast()
  const [busy, setBusy] = useState(false)
  const retenidas = useAsync(() => api.listRecordings({ estado: 'retenida', limit: 20 }), [api])
  const current = usage?.plan ?? 'free'

  async function upgrade() {
    setBusy(true)
    try {
      const disparador =
        (retenidas.data?.items.length ?? 0) > 0
          ? 'retenida'
          : usage && usage.minutosRestantes <= 0
            ? 'cuota'
            : 'plan'
      const r = await api.checkout('personal', disparador)
      if (PADDLE_CLIENT_TOKEN && r.transactionId) {
        await openPaddleCheckout(r.transactionId, () => {
          toast('¡Listo! Activando tu plan…')
          setWaiting(true)
        })
      } else if (r.url) location.assign(r.url)
      else toast('No se pudo abrir el pago. Intenta de nuevo.', 'error')
    } catch (e) {
      toast(errorText(e), 'error')
    } finally {
      setBusy(false)
    }
  }

  // Tras pagar, el webhook de Paddle activa el plan: esperar a verlo reflejado
  const [waiting, setWaiting] = useState(false)
  usePolling(
    () => {
      void refreshUsage()
      void retenidas.reload()
    },
    3000,
    waiting && current !== 'personal'
  )

  async function portal() {
    try {
      const r = await api.billingPortal()
      window.open(r.url, '_blank', 'noopener')
    } catch (e) {
      toast(errorText(e), 'error')
    }
  }

  return (
    <div className="page">
      <div className="page-head">
        <h1 className="grow">Plan y uso</h1>
      </div>
      {usage && (
        <div className="card" style={{ marginBottom: 18 }}>
          <div className="row wrap" style={{ marginBottom: 10 }}>
            <h2 className="grow">Este mes</h2>
            <span className="muted" style={{ fontSize: 13 }}>
              {formatDay(usage.periodoInicio)} – {formatDay(usage.periodoFin)}
            </span>
          </div>
          <UsageMeter usage={usage} />
          <p className="faint" style={{ fontSize: 12, marginTop: 10 }}>
            Se descuenta la duración real de cada reunión procesada. Los silencios largos del
            micrófono no se cobran dos veces.
          </p>
        </div>
      )}

      <div className="plans">
        {(org?.tipo === 'equipo' ? [] : (['free', 'personal'] as const)).map((id) => {
          const p = PLANS[id]
          const isCurrent = current === id
          return (
            <div key={id} className={`card plan stack ${isCurrent ? 'current' : ''}`}>
              <div className="row">
                <h2 className="grow">{p.nombre}</h2>
                {isCurrent && <span className="pill done">Tu plan</span>}
              </div>
              <div>
                <span className="price">{p.precioClpMes ? clp(p.precioClpMes) : '$0'}</span>
                <span className="muted"> / mes{p.precioClpMes ? ', IVA incluido' : ''}</span>
              </div>
              <ul className="muted" style={{ margin: 0, paddingLeft: 18, fontSize: 13 }}>
                <li>{formatMinutes(p.minutosIncluidos)} de reuniones al mes</li>
                <li>Reuniones de hasta {formatMinutes(p.topeGrabacionMin)}</li>
                <li>
                  Importar audios de hasta{' '}
                  {p.topeImportMb >= 1024 ? `${p.topeImportMb / 1024} GB` : `${p.topeImportMb} MB`}
                </li>
                <li>Transcripción con hablantes, notas con IA, búsqueda y exportar</li>
                <li>{p.preguntasPorDia} preguntas a la IA por día</li>
                <li>
                  {p.plantillasPersonalizadas === 1
                    ? '1 plantilla propia'
                    : `${p.plantillasPersonalizadas} plantillas propias`}{' '}
                  y {p.temas} temas a seguir
                </li>
                <li>
                  {p.webhooks
                    ? `Integraciones (Zapier, Make, n8n): hasta ${p.webhooks}`
                    : 'Sin integraciones'}
                </li>
                {id === 'personal' && <li>Si te quedas sin horas, la grabación no se pierde</li>}
              </ul>
              {id === 'personal' && !isCurrent && (
                <button className="btn primary" onClick={() => void upgrade()} disabled={busy}>
                  Mejorar a Personal
                </button>
              )}
              {id === 'personal' && isCurrent && (
                <button className="btn" onClick={() => void portal()}>
                  Facturación y cancelación
                </button>
              )}
            </div>
          )
        })}
        <div className={`card plan stack ${current === 'equipo' ? 'current' : ''}`}>
          <div className="row">
            <h2 className="grow">Equipo</h2>
            {current === 'equipo' && <span className="pill done">Tu plan</span>}
          </div>
          <div>
            <span className="price">{clp(PLANS.equipo.precioClpMes ?? 0)}</span>
            <span className="muted"> / persona al mes, IVA incluido (mínimo 2)</span>
          </div>
          <ul className="muted" style={{ margin: 0, paddingLeft: 18, fontSize: 13 }}>
            <li>{formatMinutes(PLANS.equipo.minutosIncluidos)} por persona, para todo el equipo</li>
            <li>Cada reunión es privada hasta que quien la grabó la comparte</li>
            <li>Carpetas del equipo, invitaciones y roles</li>
            <li>Una sola factura</li>
          </ul>
          <Link className="btn" to="/equipo">
            {org?.tipo === 'equipo' ? 'Administrar el equipo' : 'Crear un equipo'}
          </Link>
        </div>
      </div>

      <p className="faint" style={{ fontSize: 12, marginTop: 14 }}>
        Si un plan pagado no te sirve, escríbenos dentro de 14 días desde el primer pago y te lo
        devolvemos completo. Cancelas cuando quieras, desde aquí mismo. Ver los{' '}
        <a href={`${SITIO_URL}/terminos/`} target="_blank" rel="noreferrer">
          términos
        </a>
        .
      </p>

      {(retenidas.data?.items.length ?? 0) > 0 && (
        <div style={{ marginTop: 22 }}>
          <h2 style={{ marginBottom: 8 }}>Esperando horas</h2>
          <p className="muted" style={{ fontSize: 13 }}>
            Guardamos el audio de estas grabaciones hasta 7 días. Al mejorar tu plan se procesan
            solas.
          </p>
          <div className="rec-list">
            {retenidas.data!.items.map((r) => (
              <Link key={r.id} to={`/r/${r.id}`} className="rec-item">
                <div className="t">{r.titulo}</div>
                <span className="dur">{formatDay(r.fechaInicio)}</span>
                <StatusBadge estado={r.estado} />
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
